import { BitMask, makeMask, maskSet, trimMask } from './textGen';
import { SourceImage } from '../image/convert';

/**
 * Rasterising a shape into a sub-cell bitmap.
 *
 * This is the only part of the text generator that needs a browser: fonts are
 * the platform's, so the platform draws them. Everything downstream works on
 * the bitmap this produces, which is why the generator itself stays pure and
 * testable.
 */

export interface FontSpec {
  family: string;
  /** Height of the text in terminal rows. */
  rows: number;
  bold: boolean;
  italic: boolean;
  /** Extra space between letters, in dots. */
  letterSpacing: number;
  /** Space between lines, in dots. */
  lineSpacing: number;
}

export const DEFAULT_FONT: FontSpec = {
  family: 'Impact, "Arial Black", system-ui, sans-serif',
  rows: 5,
  bold: true,
  italic: false,
  letterSpacing: 0,
  lineSpacing: 1,
};

/**
 * The built-in box alphabet, selected like any other font.
 *
 * It is not a font the browser can draw, so the generator swaps in the stroke
 * renderer when it sees this value.
 */
export const STROKE_FONT = '__stroke__';

/**
 * The built-in joined hand. Upright and slanted are separate choices.
 *
 * There is deliberately no slanted *box* alphabet. Leaning it turns every
 * vertical stem into a staircase, and where a crossbar meets one the box
 * connectivity resolves the junction as ╬ — the same broken-looking joins the
 * rectilinear alphabet exists to avoid. The script hand survives the same
 * treatment because its strokes are already curved, so the steps read as pen
 * movement rather than as a seam.
 */
export const SCRIPT_FONT = '__script__';
export const SCRIPT_ITALIC_FONT = '__script_italic__';

/** True for any of the alphabets drawn on the grid rather than by the browser. */
export function isBuiltInFont(family: string): boolean {
  return family === STROKE_FONT || family === SCRIPT_FONT || family === SCRIPT_ITALIC_FONT;
}

export interface FontChoice {
  label: string;
  value: string;
  group: string;
  /** Roughly how wide the face runs; helps when picking for a narrow design. */
  note?: string;
}

/**
 * Fonts worth offering.
 *
 * Heavy, wide faces survive being cut down to cells; hairline faces disappear.
 * The list is grouped so the heavy ones are the obvious first pick, and every
 * entry falls back through several families so it still renders on a machine
 * that lacks the first one.
 *
 * Handwriting faces are the demanding case: their strokes are thin and their
 * letters join, so they need height and a fine character family — Braille at
 * ten rows or more — before they read as script rather than as speckle. The
 * panel says so when one is picked.
 */
export const FONT_CHOICES: readonly FontChoice[] = [
  { label: 'Box lettering', value: STROKE_FONT, group: 'Built in', note: 'narrow strokes' },
  { label: 'Script hand', value: SCRIPT_FONT, group: 'Built in', note: 'joined writing' },
  { label: 'Script hand, italic', value: SCRIPT_ITALIC_FONT, group: 'Built in', note: 'joined, leaning' },

  { label: 'Impact', value: 'Impact, "Arial Black", system-ui, sans-serif', group: 'Heavy' },
  { label: 'Arial Black', value: '"Arial Black", Impact, system-ui, sans-serif', group: 'Heavy' },
  { label: 'Haettenschweiler', value: 'Haettenschweiler, Impact, sans-serif', group: 'Heavy', note: 'condensed' },
  { label: 'Franklin Gothic', value: '"Franklin Gothic Heavy", "Arial Black", sans-serif', group: 'Heavy' },
  { label: 'Gill Sans Ultra', value: '"Gill Sans Ultra Bold", "Arial Black", sans-serif', group: 'Heavy' },

  { label: 'System sans', value: 'system-ui, "Segoe UI", Arial, sans-serif', group: 'Sans' },
  { label: 'Helvetica / Arial', value: 'Helvetica, Arial, sans-serif', group: 'Sans' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif', group: 'Sans', note: 'wide' },
  { label: 'Tahoma', value: 'Tahoma, Verdana, sans-serif', group: 'Sans' },
  { label: 'Trebuchet', value: '"Trebuchet MS", Tahoma, sans-serif', group: 'Sans' },
  { label: 'Calibri', value: 'Calibri, Candara, sans-serif', group: 'Sans' },
  { label: 'Segoe UI', value: '"Segoe UI", system-ui, sans-serif', group: 'Sans' },

  { label: 'Georgia', value: 'Georgia, "Times New Roman", serif', group: 'Serif' },
  { label: 'Times', value: '"Times New Roman", Times, serif', group: 'Serif' },
  { label: 'Garamond', value: 'Garamond, Georgia, serif', group: 'Serif' },
  { label: 'Palatino', value: '"Palatino Linotype", Palatino, Georgia, serif', group: 'Serif' },
  { label: 'Book Antiqua', value: '"Book Antiqua", Palatino, serif', group: 'Serif' },
  { label: 'Rockwell', value: 'Rockwell, "Courier New", serif', group: 'Serif', note: 'slab' },

  { label: 'Cascadia Mono', value: '"Cascadia Mono", Consolas, monospace', group: 'Monospace' },
  { label: 'Consolas', value: 'Consolas, "Cascadia Mono", monospace', group: 'Monospace' },
  { label: 'Courier', value: '"Courier New", Courier, monospace' , group: 'Monospace' },
  { label: 'Lucida Console', value: '"Lucida Console", Consolas, monospace', group: 'Monospace' },

  { label: 'Segoe Script', value: '"Segoe Script", "Bradley Hand", cursive', group: 'Handwriting' },
  { label: 'Ink Free', value: '"Ink Free", "Segoe Script", cursive', group: 'Handwriting' },
  { label: 'Brush Script', value: '"Brush Script MT", "Segoe Script", cursive', group: 'Handwriting', note: 'flowing' },
  { label: 'Lucida Handwriting', value: '"Lucida Handwriting", "Segoe Script", cursive', group: 'Handwriting' },
  { label: 'Monotype Corsiva', value: '"Monotype Corsiva", "Apple Chancery", cursive', group: 'Handwriting', note: 'elegant' },
  { label: 'Edwardian Script', value: '"Edwardian Script ITC", "Monotype Corsiva", cursive', group: 'Handwriting', note: 'very fine' },
  { label: 'Gabriola', value: 'Gabriola, "Monotype Corsiva", cursive', group: 'Handwriting', note: 'elegant' },
  { label: 'Freestyle Script', value: '"Freestyle Script", "Segoe Script", cursive', group: 'Handwriting' },
  { label: 'Bradley Hand', value: '"Bradley Hand", "Segoe Script", cursive', group: 'Handwriting' },
  { label: 'Chancery', value: '"Apple Chancery", "Monotype Corsiva", cursive', group: 'Handwriting' },
  { label: 'Generic cursive', value: 'cursive', group: 'Handwriting' },

  { label: 'Comic Sans', value: '"Comic Sans MS", "Segoe UI", sans-serif', group: 'Display' },
  { label: 'Papyrus', value: 'Papyrus, fantasy', group: 'Display' },
  { label: 'Copperplate', value: 'Copperplate, "Copperplate Gothic Light", fantasy', group: 'Display' },
  { label: 'Fantasy', value: 'fantasy', group: 'Display' },
];

export interface RasterTarget {
  /** Sub-cell resolution of the chosen fill style. */
  dotsX: number;
  dotsY: number;
  /** Cell aspect: how many times taller a cell is than it is wide. */
  cellAspect: number;
}

function canvas2d(width: number, height: number): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.ceil(width));
  canvas.height = Math.max(1, Math.ceil(height));
  return canvas.getContext('2d', { willReadFrequently: true });
}

/**
 * Renders text into a bitmap at the target's sub-cell resolution.
 *
 * Dots are not square — a cell is about twice as tall as it is wide, and a
 * Braille cell is 2 dots across but 4 down — so the canvas is drawn at the
 * dots' true aspect and the glyphs come out with the proportions they were
 * designed with instead of being squashed.
 */
export function rasterizeText(text: string, font: FontSpec, target: RasterTarget): BitMask {
  // Blank lines between text are kept, but a wholly empty input is not.
  const lines = text.split('\n').filter((line, _index, all) => line !== '' || all.length === 1);
  if (lines.length === 0) return makeMask(0, 0);

  const dotHeight = Math.max(1, Math.round(font.rows * target.dotsY));
  // One dot is `cellAspect * dotsY / dotsX` times wider than it is tall.
  const dotAspect = (target.cellAspect * target.dotsY) / target.dotsX;
  const pixelPerDotX = 1;
  const pixelPerDotY = dotAspect;

  const fontPx = dotHeight * pixelPerDotY;
  const weight = font.bold ? '800' : '400';
  const style = font.italic ? 'italic' : 'normal';
  const cssFont = `${style} ${weight} ${fontPx}px ${font.family}`;

  const measure = canvas2d(8, 8);
  if (!measure) return makeMask(0, 0);
  measure.font = cssFont;
  const widths = lines.map((line) => measure.measureText(line).width);
  const pixelWidth = Math.ceil(Math.max(1, ...widths)) + Math.abs(font.letterSpacing) * lines.length + 8;
  const lineStep = fontPx * 1.02 + font.lineSpacing * pixelPerDotY;
  const pixelHeight = Math.ceil(lineStep * lines.length + fontPx * 0.4);

  const ctx = canvas2d(pixelWidth + 8, pixelHeight + 8);
  if (!ctx) return makeMask(0, 0);
  ctx.font = cssFont;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.fillStyle = '#fff';
  if ('letterSpacing' in ctx) {
    // Supported in Chromium and Safari; ignored elsewhere, which is harmless.
    (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = `${font.letterSpacing}px`;
  }

  lines.forEach((line, i) => {
    ctx.fillText(line, 4, fontPx * 0.84 + i * lineStep + 4);
  });

  const w = ctx.canvas.width;
  const h = ctx.canvas.height;
  const pixels = ctx.getImageData(0, 0, w, h).data;

  // Collapse the pixel canvas onto the dot grid: a dot is on when its share of
  // the glyph is more than a third covered, which keeps thin strokes alive
  // without smearing them.
  const dotsWide = Math.max(1, Math.round(w / pixelPerDotX));
  const dotsHigh = Math.max(1, Math.round(h / pixelPerDotY));
  const mask = makeMask(dotsWide, dotsHigh);

  for (let dy = 0; dy < dotsHigh; dy++) {
    const y0 = Math.floor(dy * pixelPerDotY);
    const y1 = Math.max(y0 + 1, Math.floor((dy + 1) * pixelPerDotY));
    for (let dx = 0; dx < dotsWide; dx++) {
      const x0 = Math.floor(dx * pixelPerDotX);
      const x1 = Math.max(x0 + 1, Math.floor((dx + 1) * pixelPerDotX));
      let sum = 0;
      let count = 0;
      for (let y = y0; y < y1 && y < h; y++) {
        for (let x = x0; x < x1 && x < w; x++) {
          sum += pixels[(y * w + x) * 4 + 3] / 255;
          count += 1;
        }
      }
      if (count > 0 && sum / count > 0.34) maskSet(mask, dx, dy, true);
    }
  }

  return trimMask(mask);
}

export interface ImageMaskOptions {
  /** Height of the result in terminal rows. */
  rows: number;
  /** Luminance above which a pixel counts as part of the shape, 0..255. */
  threshold: number;
  /** Treat dark pixels as the shape instead of bright ones. */
  invert: boolean;
  /** Ignore pixels below this alpha. */
  alphaThreshold: number;
}

export const DEFAULT_IMAGE_MASK: ImageMaskOptions = {
  rows: 8,
  threshold: 128,
  invert: false,
  alphaThreshold: 24,
};

/**
 * Rasterises an image — a logo, usually — into the same kind of bitmap.
 *
 * Alpha is honoured first: a logo exported with a transparent background gives
 * a clean shape regardless of what colour it is drawn in.
 */
export function rasterizeImage(
  src: SourceImage,
  options: ImageMaskOptions,
  target: RasterTarget,
): BitMask {
  const dotsHigh = Math.max(1, Math.round(options.rows * target.dotsY));
  const dotAspect = (target.cellAspect * target.dotsY) / target.dotsX;
  // Preserve the picture's proportions in dot space.
  const dotsWide = Math.max(1, Math.round((src.width / src.height) * dotsHigh * dotAspect));

  const mask = makeMask(dotsWide, dotsHigh);
  const scaleX = src.width / dotsWide;
  const scaleY = src.height / dotsHigh;

  for (let dy = 0; dy < dotsHigh; dy++) {
    const y0 = Math.floor(dy * scaleY);
    const y1 = Math.max(y0 + 1, Math.floor((dy + 1) * scaleY));
    for (let dx = 0; dx < dotsWide; dx++) {
      const x0 = Math.floor(dx * scaleX);
      const x1 = Math.max(x0 + 1, Math.floor((dx + 1) * scaleX));
      let lum = 0;
      let alpha = 0;
      let count = 0;
      for (let y = y0; y < y1 && y < src.height; y++) {
        for (let x = x0; x < x1 && x < src.width; x++) {
          const i = (y * src.width + x) * 4;
          lum += 0.2126 * src.data[i] + 0.7152 * src.data[i + 1] + 0.0722 * src.data[i + 2];
          alpha += src.data[i + 3];
          count += 1;
        }
      }
      if (count === 0) continue;
      if (alpha / count < options.alphaThreshold) continue;
      const bright = lum / count >= options.threshold;
      if (bright !== options.invert) maskSet(mask, dx, dy, true);
    }
  }

  return trimMask(mask);
}
